import { useState, useEffect } from 'react';
import { Lead, LeadStatus, LeadOutcome } from '../../types/LeadTypes';

interface UseLeadListStateProps {
  leads: Lead[];
  onStatusChange: (id: string, status: LeadStatus) => void;
  onOutcomeChange: (id: string, outcome: LeadOutcome | null) => void;
  onValueChange: (id: string, value: string) => void;
  onNameChange: (id: string, name: string) => void;
}

export function useLeadListState({
  leads,
  onStatusChange,
  onOutcomeChange,
  onValueChange,
  onNameChange
}: UseLeadListStateProps) {
  const [openLeads, setOpenLeads] = useState<Record<string, boolean>>({});
  const [editingName, setEditingName] = useState<string | null>(null);
  const [transcriptModalOpen, setTranscriptModalOpen] = useState(false);
  const [activeLeadId, setActiveLeadId] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [paginatedLeads, setPaginatedLeads] = useState<Lead[]>([]);
  
  useEffect(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    setPaginatedLeads(leads.slice(startIndex, endIndex));
  }, [leads, pageSize, currentPage]);
  
  const toggleLead = (id: string) => {
    setOpenLeads(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const openNameModal = (id: string) => {
    setEditingName(id);
  };

  const closeNameModal = () => {
    setEditingName(null);
  };

  const handleNameChange = (id: string, newName: string) => {
    onNameChange(id, newName);
    closeNameModal();
  };
  
  const openTranscriptModal = (id: string) => {
    setActiveLeadId(id);
    setTranscriptModalOpen(true);
  };
  
  const closeTranscriptModal = () => {
    setTranscriptModalOpen(false);
    setActiveLeadId(null);
  };
  
  const handleStatusChange = (id: string, status: LeadStatus) => {
    onStatusChange(id, status);
  };
  
  const handleOutcomeChange = (id: string, outcome: LeadOutcome | null) => {
    onOutcomeChange(id, outcome);
  };
  
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    setOpenLeads({});
  };
  
  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(1);
    setOpenLeads({});
  };
  
  const totalPages = Math.ceil(leads.length / pageSize);
  
  return {
    paginatedLeads,
    openLeads,
    editingName,
    transcriptModalOpen,
    activeLeadId,
    currentPage,
    pageSize,
    totalPages,
    toggleLead,
    openNameModal,
    closeNameModal,
    handleNameChange,
    openTranscriptModal,
    closeTranscriptModal,
    handleStatusChange,
    handleOutcomeChange,
    handlePageChange,
    handlePageSizeChange
  };
}
